/**
 * Elegir una o varias marcas para filtrar la tabla de productos (ADM-02).
 *
 * Mismo trato que `SelectorCategorias`: se monta solo mientras esta abierto y
 * no toca la tabla hasta "Aplicar". Las elegidas van arriba de la lista, para
 * no tener que buscarlas de nuevo entre cientos de marcas al volver a abrir.
 */
import { useState } from 'react';

import type { OpcionFiltroProductos } from '@bandu/compartido';

import { numero } from '../../utilidades/formato';
import { contieneTexto } from '../../utilidades/texto-busqueda';
import { Modal } from './Modal';

export interface SelectorMarcasProps {
  marcas: OpcionFiltroProductos[];
  elegidas: string[];
  onAplicar(marcas: string[]): void;
  onCerrar(): void;
}

export function SelectorMarcas({ marcas, elegidas, onAplicar, onCerrar }: SelectorMarcasProps) {
  const [marcadas, setMarcadas] = useState(() => new Set(elegidas));
  const [busqueda, setBusqueda] = useState('');

  // El orden de "arriba las elegidas" se fija al abrir: si se reordenara al
  // marcar, la fila saltaria de lugar bajo el dedo.
  const [primero] = useState(() => new Set(elegidas));
  const ordenadas = [
    ...marcas.filter((marca) => primero.has(marca.slug)),
    ...marcas.filter((marca) => !primero.has(marca.slug)),
  ];
  const visibles = ordenadas.filter((marca) => contieneTexto(marca.nombre, busqueda));

  function marcar(slug: string, marcada: boolean): void {
    setMarcadas((actual) => {
      const siguiente = new Set(actual);
      if (marcada) siguiente.add(slug);
      else siguiente.delete(slug);
      return siguiente;
    });
  }

  return (
    <Modal titulo="Filtrar por marca" bajada="Puedes elegir varias" abierto onCerrar={onCerrar}>
      <label className="solo-lectores" htmlFor="buscar-marca">
        Buscar marca
      </label>
      <input
        id="buscar-marca"
        type="search"
        className="entrada entrada-ancha"
        placeholder="Buscar marca"
        value={busqueda}
        onChange={(evento) => {
          setBusqueda(evento.target.value);
        }}
      />

      <ul className="lista-marcable" aria-label="Marcas">
        {visibles.length === 0 ? (
          <li className="lista-marcable-vacia">Ninguna marca coincide con "{busqueda}".</li>
        ) : null}
        {visibles.map((marca) => (
          <li key={marca.slug}>
            <label className="fila-marcable">
              <input
                type="checkbox"
                checked={marcadas.has(marca.slug)}
                onChange={(evento) => {
                  marcar(marca.slug, evento.target.checked);
                }}
              />
              <span className="nombre">{marca.nombre}</span>
              <span className="cuenta">{numero(marca.cantidad)}</span>
            </label>
          </li>
        ))}
      </ul>

      <div className="modal-acciones separadas">
        <button
          type="button"
          className="btn btn-suave"
          disabled={marcadas.size === 0}
          onClick={() => {
            setMarcadas(new Set());
          }}
        >
          Desmarcar todo
        </button>
        <span className="acciones-fila">
          <button type="button" className="btn btn-contorno" onClick={onCerrar}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primario"
            onClick={() => {
              onAplicar(Array.from(marcadas));
            }}
          >
            Aplicar{marcadas.size > 0 ? ` (${marcadas.size})` : ''}
          </button>
        </span>
      </div>
    </Modal>
  );
}
